import { LabelWithoutRef } from '../../../label/label-without-ref';
import { getContentLabel } from '../utils/get-content-label';

import { cn } from '../../../../utils/cn';
import { ComboboxModel, type ComboboxModelModel } from '../../../combobox/combobox-model';
import type {
  ScheduleActionContent,
  ScheduleActionModule,
  ScheduleActionReference,
  ScheduleActionReferenceErrors,
} from '../types';

interface ReferenceFieldProps {
  contents: ScheduleActionContent[];
  disabled?: boolean;
  errors?: ScheduleActionReferenceErrors;
  id: string;
  label: string;
  isModuleEditable?: boolean;
  modules: ScheduleActionModule[];
  onChange: (value: ScheduleActionReference) => void;
  value: ScheduleActionReference;
}

export function ReferenceField({
  contents,
  disabled = false,
  errors,
  id,
  label,
  isModuleEditable = true,
  modules,
  onChange,
  value,
}: ReferenceFieldProps) {
  const moduleModel = modules.map((module) => ({
    value: module.id,
    item: (
      <div className="grid p-1">
        {module.title && <p className="truncate font-medium">{module.title}</p>}
        {module.description && (
          <p className="line-clamp-2 text-xs text-muted-foreground">{module.description}</p>
        )}
      </div>
    ),
  }));
  const contentModel: ComboboxModelModel = contents.map((content) => {
    const description = typeof content.description === 'string' ? content.description : undefined;
    const label = getContentLabel(content);

    return {
      value: String(content.id),
      item: (
        <div className="grid p-1">
          {label && <p className="truncate font-medium">{label}</p>}
          {description && (
            <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      ),
    };
  });
  const hasSelectedContent = contentModel.some((modelItem) => {
    return 'value' in modelItem && modelItem.value === value.contentId;
  });

  // The edit form already has a contentId before the dependent options finish loading.
  // Keep it in the model so the controlled select can render the selection immediately.
  if (value.contentId && !hasSelectedContent) {
    contentModel.unshift({
      value: value.contentId,
      item: value.contentId,
    });
  }

  return (
    <div className="space-y-1">
      <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
        {label}
      </LabelWithoutRef>
      {isModuleEditable && (
        <ComboboxModel
          classNameCommandList="max-h-[min(15rem,55vh)]"
          className={cn(
            'h-auto min-h-9 py-1 text-left [&>span]:items-start [&>span>span]:w-full [&>span>span]:min-w-0 [&>span>span]:whitespace-normal',
            errors?.moduleId && 'border-destructive',
          )}
          disabled={disabled}
          id={`${id}-module`}
          model={moduleModel}
          value={value.moduleId}
          onSelect={(moduleId) => onChange({ moduleId, contentId: '' })}
        />
      )}
      <ComboboxModel
        classNameCommandList="max-h-[min(15rem,55vh)]"
        className={cn(
          'h-auto min-h-9 py-1 text-left [&>span]:items-start [&>span>span]:w-full [&>span>span]:min-w-0 [&>span>span]:whitespace-normal',
          errors?.contentId && 'border-destructive',
        )}
        key={`${value.moduleId}-${value.contentId}-${contentModel.length}`}
        disabled={disabled || !value.moduleId}
        id={`${id}-content`}
        model={contentModel}
        value={value.contentId}
        onSelect={(contentId) => onChange({ ...value, contentId })}
      />
    </div>
  );
}
